import { formatPrice } from '@zetter/shared';
import { DOC_KINDS, LANDING_COUNT, TEMPLATE_TOTAL } from './catalog.js';
import { CLUSTERS, type Cluster } from './clusters.js';
import type { PublicTemplate } from './public-data.js';
import { faqItems } from './faq.js';
import type { PublicData } from './public-data.js';

/**
 * Разметка для поиска и для машин, которые отвечают вместо поиска.
 *
 * Здесь собрано всё, что уходит наружу помимо самой страницы: заголовки и
 * описания, ссылки-канониклы, schema.org, robots.txt, sitemap.xml и llms.txt.
 * В одном модуле, потому что источник у них общий — состав публичных страниц.
 * Разъехавшись, они дают самую дорогую ошибку этого слоя: страница в sitemap,
 * закрытая в robots.txt, или каноникл, ведущий на адрес, которого нет.
 *
 * Модуль не знает ни про Vue, ни про DOM: его зовут и при отрисовке страницы,
 * и из сборки (`prerender.mjs`), где ни того, ни другого нет.
 */

/** Публичная страница: маршрут, имя файла в `dist` и её обвязка. */
export interface PageSeo {
  path: string;
  /** Имя файла, который положит пререндер. nginx ищет его как `$uri.html`. */
  file: string;
  title: string;
  description: string;
  /** Список для `meta name="keywords"`. Пусто — тега не будет вовсе. */
  keywords?: string;
  /** Для sitemap.xml: Яндекс их читает, Google игнорирует. */
  priority: string;
  changefreq: string;
}

export const SITE_NAME = 'Keypi';

/**
 * Обложка для ссылок.
 *
 * Её видят Telegram, WhatsApp, Slack и выдача, когда ссылку куда-нибудь
 * вставляют. Одна на все публичные страницы: правовые документы разделяют
 * обложку с витриной намеренно — рисовать «Публичную оферту» отдельной
 * картинкой незачем, а ссылкой делятся всё равно на продукт.
 *
 * Файл лежит в `public/og.png` готовым; собирается он из элементов лендинга
 * (`og/cover.html`, пересобрать — `node og/render.mjs`). Размер вдвое больше
 * холста 1200×630: площадки показывают картинку на плотных экранах, и снимок
 * один к одному выглядит на них мылом.
 */
const OG_IMAGE = { path: '/og.png', width: 2400, height: 1260 };

const OG_IMAGE_ALT =
  'Витрина Keypi: поле задачи «КП на монтаж вентиляции» и собранное ' +
  'коммерческое предложение со сметой рядом.';

/**
 * Короткое описание продукта одной фразой.
 *
 * Отдельной константой, потому что расходится оно дороже всего: эту строку
 * читают описание в выдаче, og:description, llms.txt и schema.org, и в ответе
 * ассистента она чаще всего и оказывается.
 */
export const SITE_TAGLINE =
  'ИИ-агент собирает коммерческое предложение, презентацию, лендинг или прайс из ваших ' +
  'материалов — брифа, таблицы с ценами, записи созвона.';

export const PAGES: PageSeo[] = [
  {
    path: '/',
    file: 'index.html',
    /*
     * Заголовок начинается с запроса, а не с имени. Марка новая, её никто не
     * ищет, и первые слова обязаны совпадать с тем, что человек набрал. Три
     * типа документа вместо одного — потому что каталог шире, чем КП, и раньше
     * страница об этом молчала.
     */
    /*
     * Заголовок витрины намеренно широкий. Запросы «коммерческое предложение
     * онлайн», «нейросеть для презентаций» и «создать лендинг» забирают
     * посадочные страницы; борясь за них же, витрина конкурировала бы со
     * своими, и поиск выбирал бы победителя сам — обычно не того.
     */
    title: 'Keypi — нейросеть для документов клиенту: КП, презентации, лендинги',
    description:
      `${SITE_TAGLINE} Опишите задачу прямо на главной — регистрация потом. Через час — ` +
      `готовый документ в фирменном стиле: ссылкой, в PDF или PPTX. Правки словами, без ` +
      `дизайнера и вёрстки. ${TEMPLATE_TOTAL} шаблонов, интеграция с amoCRM. Первый документ ` +
      'бесплатно.',
    keywords:
      'коммерческое предложение, сделать КП, лендинг, создать лендинг, презентация компании, ' +
      'прайс-лист, кейс проекта, нейросеть для коммерческих предложений, ИИ для презентаций, ' +
      'шаблоны документов, коммерческое предложение из amoCRM',
    priority: '1.0',
    changefreq: 'weekly',
  },
  {
    path: '/offer',
    file: 'offer.html',
    title: 'Публичная оферта — Keypi',
    description:
      'Договор оказания услуг сервиса Keypi: предмет, стоимость документа, порядок оплаты, ' +
      'права на результат и ответственность сторон.',
    priority: '0.3',
    changefreq: 'yearly',
  },
  {
    path: '/payment-rules',
    file: 'payment-rules.html',
    title: 'Правила приёма платежей — Keypi',
    description:
      'Чем и как оплачивается подписка Keypi: карта, счёт для юридических лиц, автоплатёж, ' +
      'суммы платежа и порядок возврата.',
    priority: '0.3',
    changefreq: 'yearly',
  },
  {
    path: '/privacy',
    file: 'privacy.html',
    title: 'Политика обработки персональных данных — Keypi',
    description:
      'Какие персональные данные обрабатывает Keypi, зачем, сколько хранит и кому передаёт, ' +
      'включая трансграничную передачу поставщику моделей.',
    priority: '0.3',
    changefreq: 'yearly',
  },
  ...CLUSTERS.map((cluster) => ({
    path: `/${cluster.slug}`,
    file: `${cluster.slug}.html`,
    title: cluster.title,
    description: cluster.description,
    keywords: cluster.keywords,
    // Ниже витрины, но заметно выше правовых документов: это рабочие входы в
    // продукт, и обходить их поиск должен чаще, чем оферту.
    priority: '0.8',
    changefreq: 'monthly',
  })),
];

export function pageFor(path: string): PageSeo {
  return PAGES.find((page) => page.path === path) ?? (PAGES[0] as PageSeo);
}

/**
 * Разделы, закрытые от индексации.
 *
 * Кабинет в выдаче не нужен никому: за каждым адресом стоит форма входа, и
 * краулер получает её вместо содержимого. А `/d/`, `/doc/` и `/preview/` —
 * документы клиентов заказчика: они открываются по ссылке без входа, и попасть
 * в поиск не должны ни при каких обстоятельствах.
 *
 * Список повторяется в nginx заголовком `X-Robots-Tag`, и это не дублирование:
 * robots.txt просит не заходить, заголовок запрещает индексировать то, куда
 * всё-таки зашли по ссылке со стороны.
 */
const DISALLOWED = [
  '/api/',
  '/admin',
  '/billing',
  '/catalog',
  '/collection/',
  '/collections',
  '/d/',
  '/doc/',
  '/embed/',
  '/forgot-password',
  '/integrations',
  '/invite/',
  '/login',
  '/members',
  '/onboarding',
  '/preview/',
  '/projects',
  '/register',
  '/reset-password/',
  '/verify-email/',
];

/**
 * Краулеры, которым доступ разрешается ИМЕНЕМ.
 *
 * Формально они попадают и под `User-agent: *`, но именная группа отменяет
 * общую целиком, и часть из них читает только свою. Явное разрешение — ещё и
 * заявление о намерении: `Google-Extended` и `Applebot-Extended` управляют не
 * обходом, а правом использовать содержимое в обучении и в ответах ассистента.
 * Нам это ровно то, что нужно: продукт должен попадать в ответ, когда человек
 * спрашивает, чем собрать коммерческое предложение.
 */
const AI_AGENTS = [
  'Googlebot',
  'Google-Extended',
  'Bingbot',
  'YandexBot',
  'GPTBot',
  'OAI-SearchBot',
  'ChatGPT-User',
  'ClaudeBot',
  'Claude-User',
  'Claude-SearchBot',
  'anthropic-ai',
  'PerplexityBot',
  'Perplexity-User',
  'Applebot',
  'Applebot-Extended',
  'Amazonbot',
  'meta-externalagent',
  'MistralAI-User',
  'DuckAssistBot',
  'cohere-ai',
  'CCBot',
];

export function robotsTxt(origin: string): string {
  const rules = DISALLOWED.map((path) => `Disallow: ${path}`).join('\n');
  const named = AI_AGENTS.map((agent) => `User-agent: ${agent}`).join('\n');

  return `# ${origin}
# Публичная часть открыта всем, включая краулеры ИИ-ассистентов: ответ про то,
# чем собрать коммерческое предложение, должен опираться на нашу страницу, а не
# на чужой пересказ. Кабинет и документы клиентов закрыты — там за каждым
# адресом либо форма входа, либо чужие данные.

User-agent: *
${rules}
Allow: /

${named}
${rules}
Allow: /

Sitemap: ${origin}/sitemap.xml
`;
}

/**
 * Страницы каталога: сам каталог и по одной на шаблон.
 *
 * Считаются из данных, а не перечислены руками, и это единственный способ:
 * шаблоны заводит суперадмин в админке, а не выкладка сайта. Список руками
 * означал бы, что новый шаблон появляется в каталоге и не появляется в
 * поиске — то есть половину смысла публичного каталога.
 *
 * Ради чего это вообще: название каждого шаблона — готовый поисковый запрос
 * («шаблон коммерческого предложения», «презентация услуг»). Пока каталог
 * закрыт входом, на эти запросы отвечает кто угодно, кроме нас.
 */
export function catalogPages(templates: PublicTemplate[]): PageSeo[] {
  const index: PageSeo = {
    path: '/templates',
    file: 'templates.html',
    title: `Шаблоны документов — ${templates.length || TEMPLATE_TOTAL} готовых макетов — Keypi`,
    description:
      'Каталог готовых шаблонов: коммерческие предложения, презентации, лендинги, ' +
      'прайс-листы, кейсы. Каждый можно посмотреть целиком до регистрации, а потом ' +
      'переделать под себя словами — нейросеть соберёт документ за час.',
    keywords:
      'шаблоны документов, шаблон коммерческого предложения, шаблон презентации, ' +
      'шаблон лендинга, макеты кп, готовые шаблоны',
    priority: '0.9',
    changefreq: 'weekly',
  };

  const pages = templates.map((template) => ({
    path: `/templates/${template.slug}`,
    file: `templates/${template.slug}.html`,
    title: `${template.name} — шаблон${template.docType ? ` ${template.docType.toLowerCase()}` : ''} — Keypi`,
    description:
      template.description ??
      `${template.name}: готовый макет, который нейросеть переделает под вашу задачу за час.`,
    priority: '0.7',
    changefreq: 'monthly',
  }));

  return [index, ...pages];
}

export function sitemapXml(origin: string, lastmod: string, extra: PageSeo[] = []): string {
  const urls = [...PAGES, ...extra].map(
    (page) => `  <url>
    <loc>${origin}${page.path}</loc>
    <lastmod>${lastmod}</lastmod>
    <changefreq>${page.changefreq}</changefreq>
    <priority>${page.priority}</priority>
  </url>`,
  ).join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${urls}
</urlset>
`;
}

/**
 * Справка о продукте для языковых моделей (llmstxt.org).
 *
 * Формат придуман ровно для нашего случая: ассистент, отвечающий на «чем
 * собрать КП», читает не страницу с версткой, а короткий текст с фактами. Всё
 * ниже — пересказ того, что написано на лендинге; выдумывать здесь нечего, и
 * расхождение с обещаниями страницы дороже любой найденной формулировки.
 *
 * Цены подставляются только настоящие. Пришла сборка без доступа к API — блок
 * про деньги не пишется вовсе: прочерк вместо цены машина процитирует так же
 * охотно, как число.
 */
export function llmsTxt(origin: string, data: PublicData): string {
  const lines = [
    `# ${SITE_NAME}`,
    '',
    `> ${SITE_TAGLINE} Через час — готовый документ в фирменном стиле: по ссылке, ` +
      'в PDF или в PPTX.',
    '',
    'Keypi — российский веб-сервис для отделов продаж, агентств и студий, которые делают ' +
      'коммерческие предложения, лендинги, презентации и прайсы каждую неделю. Вместо шаблона ' +
      'в PowerPoint, конструктора сайтов и очереди к дизайнеру человек присылает материалы, ' +
      'отвечает на вопросы агента и получает свёрстанный документ. Интерфейс и поддержка — на ' +
      'русском языке.',
    '',
    '## Что собирает',
    '',
    `В каталоге ${TEMPLATE_TOTAL} готовых шаблонов; можно начать и с чистого листа. Любой ` +
      'сохранённый проект становится шаблоном организации, и дальше агент собирает по нему.',
    '',
    ...DOC_KINDS.map(
      (kind) =>
        `- **${kind.name}** (шаблонов: ${kind.count}) — ${kind.note}` +
        (kind.examples ? ` Заготовки: ${kind.examples}.` : ''),
    ),
    '',
    '## Как это работает',
    '',
    '1. Опишите задачу — можно прямо на главной странице, не заводя аккаунт: текст ' +
      'сохранится и будет ждать после регистрации.',
    '2. Пришлите материалы: бриф, прайс, макет, запись созвона. Файлы агент читает сам, ' +
      'аудио расшифровывает по говорящим и вытаскивает задачи, сроки и цифры.',
    '3. Ответьте на вопросы. Агент спрашивает только то, чего не хватает; пропущенное ' +
      'решает сам и перечисляет, что решил за вас.',
    '4. Правьте. Суть — словами в чате или голосом. Текст и блоки — руками прямо в превью, ' +
      'бесплатно. Каждая правка сохраняется отдельной версией с откатом в один клик.',
    '5. Отправьте. Ссылка всегда показывает актуальную версию, видно, что клиент её открыл. ' +
      'PDF и PPTX собираются из того же документа.',
    '',
    '## Что важно знать',
    '',
    '- Первая версия документа готова примерно через 15 минут, весь цикл с правками — около часа.',
    '- Выгрузка: публичная ссылка, PDF, PPTX. Ссылка отзывается в один клик.',
    '- Фирменный стиль: готовый проект сохраняется шаблоном организации, дальше агент ' +
      'собирает по нему. Свои шаблоны видны только своей команде.',
    '- Правки текста и блоков руками в превью не тарифицируются и не ждут агента.',
    '- Проекты видны только своей организации; доступ уволившегося сотрудника закрывается сразу.',
    '- Есть интеграция с amoCRM и Kommo: документ собирается прямо из карточки сделки.',
    `- Лендингов в каталоге ${LANDING_COUNT}: у опубликованной страницы есть форма заявки, ` +
      'а открыть её можно на своём домене.',
    '- Старт без карты: пробный план бесплатный, а новой организации начисляются документы для знакомства.',
  ];

  const terms = data.legal?.terms ?? null;
  const plans = data.pricing?.plans ?? [];

  if (terms) {
    lines.push(
      '',
      '## Сколько стоит',
      '',
      `- Единица счёта — готовый документ, а не токены и не правки: ${formatPrice(terms.creditKopecks)} ` +
        'за документ. Списывается на первой сборке, правки к готовому документу бесплатны.',
      `- Документ по своему шаблону организации — ${formatPrice(terms.childCreditKopecks)}.`,
      `- Новой организации начисляется документов в подарок: ${terms.welcomeCredits}.`,
      '- Подписка включает пакет документов на месяц; перерасход докупается по той же цене.',
    );

    if (plans.length > 0) {
      lines.push(
        '',
        'Планы:',
        '',
        ...plans.map(
          (plan) =>
            `- ${plan.title} — ${formatPrice(plan.monthlyPriceKopecks)} в месяц, ` +
            // Ноль включённых документов пишется словами, как и на странице цен:
            // «включено документов: 0» читается как ошибка выгрузки, а не как
            // условие плана.
            (plan.includedCredits === 0
              ? 'без включённых документов.'
              : `включено документов: ${plan.includedCredits}.`),
        ),
      );
    }
  }

  lines.push(
    '',
    '## Страницы под задачу',
    '',
    ...CLUSTERS.map((cluster) => `- [${cluster.title}](${origin}/${cluster.slug}): ${cluster.lede}`),
    `- [Каталог шаблонов](${origin}/templates): все готовые макеты, каждый видно целиком до регистрации.`,
    '',
    '## Частые вопросы',
    '',
    ...faqItems(data).flatMap((item) => [`**${item.q}**`, '', item.a, '']),
    '## Ссылки',
    '',
    ...PAGES.map((page) => `- [${page.title}](${origin}${page.path}): ${page.description}`),
    '',
  );

  return lines.join('\n');
}

/**
 * schema.org для лендинга.
 *
 * Один граф, а не три тега: связи между организацией, сайтом и продуктом важнее
 * самих сущностей — по ним ассистент понимает, что цена относится к продукту, а
 * продукт принадлежит юридическому лицу с ИНН.
 *
 * Ни отзывов, ни рейтинга здесь нет и не будет, пока их не будет на самом деле:
 * выдуманный `aggregateRating` — единственная разметка, за которую площадки
 * снимают сайт с расширенных результатов целиком.
 */
export function landingJsonLd(origin: string, data: PublicData): string {
  const company = data.legal?.company ?? null;
  const organizationId = `${origin}/#organization`;

  const graph: Record<string, unknown>[] = [];

  if (company) {
    graph.push({
      '@type': 'Organization',
      '@id': organizationId,
      name: company.name,
      legalName: company.fullName || company.name,
      url: origin,
      ...(company.inn ? { taxID: company.inn } : {}),
      ...(company.address
        ? { address: { '@type': 'PostalAddress', streetAddress: company.address, addressCountry: 'RU' } }
        : {}),
      ...(company.phone || company.email
        ? {
            contactPoint: {
              '@type': 'ContactPoint',
              contactType: 'customer support',
              areaServed: 'RU',
              availableLanguage: 'Russian',
              ...(company.phone ? { telephone: company.phone } : {}),
              ...(company.email ? { email: company.email } : {}),
            },
          }
        : {}),
    });
  }

  graph.push({
    '@type': 'WebSite',
    '@id': `${origin}/#website`,
    url: origin,
    name: SITE_NAME,
    description: SITE_TAGLINE,
    inLanguage: 'ru-RU',
    ...(company ? { publisher: { '@id': organizationId } } : {}),
  });

  const plans = data.pricing?.plans ?? [];
  graph.push({
    '@type': 'SoftwareApplication',
    '@id': `${origin}/#product`,
    name: SITE_NAME,
    url: origin,
    image: `${origin}${OG_IMAGE.path}`,
    description: `${SITE_TAGLINE} Через час — документ в фирменном стиле: по ссылке, в PDF или в PPTX.`,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    inLanguage: 'ru-RU',
    featureList: [
      ...DOC_KINDS.map((kind) => `${kind.name}: ${kind.count} готовых шаблонов`),
      'Расшифровка записи созвона по говорящим',
      'Правки текстом и голосом без дизайнера и вёрстки',
      'Версии документа с откатом',
      'Публичная ссылка, PDF и PPTX',
      'Шаблоны фирменного стиля организации',
      'Форма заявки на опубликованной странице',
      'Интеграция с amoCRM и Kommo',
    ],
    ...(company ? { provider: { '@id': organizationId } } : {}),
    ...(plans.length > 0
      ? {
          offers: {
            '@type': 'AggregateOffer',
            priceCurrency: 'RUB',
            offerCount: plans.length,
            lowPrice: (Math.min(...plans.map((plan) => plan.monthlyPriceKopecks)) / 100).toFixed(0),
            highPrice: (Math.max(...plans.map((plan) => plan.monthlyPriceKopecks)) / 100).toFixed(0),
            offers: plans.map((plan) => ({
              '@type': 'Offer',
              name: plan.title,
              price: (plan.monthlyPriceKopecks / 100).toFixed(0),
              priceCurrency: 'RUB',
              category: 'subscription',
              url: `${origin}/#pricing`,
            })),
          },
        }
      : {}),
  });

  /*
   * Частые вопросы размечаются ТЕМ ЖЕ текстом, который стоит на странице
   * (`faq.ts` — общий источник для обоих). Разметка, разошедшаяся с видимым
   * текстом, не просто бесполезна: за неё снимают расширенные результаты
   * целиком.
   */
  graph.push({
    '@type': 'FAQPage',
    '@id': `${origin}/#faq`,
    inLanguage: 'ru-RU',
    isPartOf: { '@id': `${origin}/#website` },
    mainEntity: faqItems(data).map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  });

  return jsonLd({ '@context': 'https://schema.org', '@graph': graph });
}

/**
 * schema.org для посадочной страницы.
 *
 * `FAQPage` здесь тем же текстом, что стоит на странице (`clusters.ts` —
 * общий источник), и по той же причине, что на витрине: разметка, разошедшаяся
 * с видимым текстом, стоит расширенных результатов целиком.
 */
export function clusterJsonLd(origin: string, page: PageSeo, cluster: Cluster): string {
  return jsonLd({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        url: `${origin}${page.path}`,
        name: page.title,
        description: page.description,
        inLanguage: 'ru-RU',
        isPartOf: { '@id': `${origin}/#website` },
        about: { '@id': `${origin}/#product` },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: SITE_NAME, item: origin },
            { '@type': 'ListItem', position: 2, name: cluster.navLabel },
          ],
        },
      },
      {
        '@type': 'HowTo',
        name: cluster.stepsTitle,
        description: cluster.lede,
        inLanguage: 'ru-RU',
        step: cluster.steps.map((step, index) => ({
          '@type': 'HowToStep',
          position: index + 1,
          name: step.title,
          text: step.text,
        })),
      },
      {
        '@type': 'FAQPage',
        inLanguage: 'ru-RU',
        mainEntity: cluster.faq.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  });
}

/** schema.org для правового документа: что это за страница и где она в сайте. */
export function legalJsonLd(origin: string, page: PageSeo): string {
  return jsonLd({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    url: `${origin}${page.path}`,
    name: page.title,
    description: page.description,
    inLanguage: 'ru-RU',
    isPartOf: { '@id': `${origin}/#website` },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: SITE_NAME, item: origin },
        { '@type': 'ListItem', position: 2, name: page.title },
      ],
    },
  });
}

/**
 * JSON внутри `<script>`.
 *
 * Экранируется `<`, а не кавычки: последовательность `</script>` внутри строки
 * закрыла бы тег и вывалила остаток разметки в текст страницы.
 */
function jsonLd(value: unknown): string {
  return JSON.stringify(value, null, 2).replace(/</g, '\\u003c');
}

/**
 * Содержимое `<head>` публичной страницы.
 *
 * Собирается строкой, а не через DOM: разметку пишет пререндер, и в момент
 * сборки ни `document`, ни браузера нет. Клиент эти теги не трогает — они уже
 * в файле, и переписывать их после загрузки бандла значило бы отдавать
 * краулеру пустую страницу и надеяться, что он дождётся скрипта.
 */
export function headHtml(origin: string, page: PageSeo, data: PublicData): string {
  const url = `${origin}${page.path}`;
  const cluster = CLUSTERS.find((item) => `/${item.slug}` === page.path);
  const structured =
    page.path === '/'
      ? landingJsonLd(origin, data)
      : cluster
        ? clusterJsonLd(origin, page, cluster)
        : legalJsonLd(origin, page);

  return [
    `<meta name="description" content="${attr(page.description)}" />`,
    /*
     * `keywords` заполнен, и трезво: Google игнорирует его официально с 2009
     * года, Bing считает признаком спама, у Яндекса он числится как «может
     * учитываться» и практического веса не имеет. Стоит он здесь коротким и
     * честным списком того, о чём страница и правда написана, — набивать его
     * бессмысленно вдвойне, а вреда от десятка настоящих фраз нет.
     */
    ...(page.keywords ? [`<meta name="keywords" content="${attr(page.keywords)}" />`] : []),
    /*
     * `max-snippet:-1` — не косметика. Он снимает ограничение на длину
     * цитаты, и именно от него зависит, процитирует ассистент фразу про
     * продукт целиком или оборвёт её на середине.
     */
    '<meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />',
    `<link rel="canonical" href="${attr(url)}" />`,
    `<meta property="og:type" content="website" />`,
    `<meta property="og:site_name" content="${attr(SITE_NAME)}" />`,
    `<meta property="og:locale" content="ru_RU" />`,
    `<meta property="og:url" content="${attr(url)}" />`,
    `<meta property="og:title" content="${attr(page.title)}" />`,
    `<meta property="og:description" content="${attr(page.description)}" />`,
    `<meta property="og:image" content="${attr(origin + OG_IMAGE.path)}" />`,
    `<meta property="og:image:type" content="image/png" />`,
    `<meta property="og:image:width" content="${OG_IMAGE.width}" />`,
    `<meta property="og:image:height" content="${OG_IMAGE.height}" />`,
    `<meta property="og:image:alt" content="${attr(OG_IMAGE_ALT)}" />`,
    // `summary_large_image`, а не `summary`: с обычной карточкой обложка
    // сжимается в квадратик рядом с текстом, и от композиции остаётся угол.
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${attr(page.title)}" />`,
    `<meta name="twitter:description" content="${attr(page.description)}" />`,
    `<meta name="twitter:image" content="${attr(origin + OG_IMAGE.path)}" />`,
    `<meta name="twitter:image:alt" content="${attr(OG_IMAGE_ALT)}" />`,
    `<script type="application/ld+json">${structured}</script>`,
  ].join('\n    ');
}

/** Значение атрибута: кавычки и угловые скобки закрыли бы тег раньше времени. */
function attr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
